import {
    WindSpeed,
    type Vec2,
    getUnitVector,
    getDotProduct,
    multiplyMagnitude,
    type Coords,
    Terrain,
    Logger,
    LogLevel,
    getWindConstantsWithOverrides
    } from "@firesim/simkit-core"
import { TerrainCodes } from './terrain-types';

export type {Coords};

const logger = new Logger({logLevel: LogLevel.Error, owner: 'wind-influence'});

export type WindData = Record<WindSpeed, number[]>;
export const baseWindInfluence: Record<WindSpeed, number[]> = {
    [WindSpeed.None]: [0.9, 0.9, 0.9, 0.9, 0.9],
    [WindSpeed.Light]: [1, 0.85, 0.77, 0.75, 0.75],
    [WindSpeed.Moderate]: [1.5, 0.80, 0.65, 0.55, 0.50],
    [WindSpeed.Strong]: [1.8, 0.70, 0.60, 0.5, 0.4]
};

export const baseEmberThrowWeight: Record<WindSpeed, number> = {
    [WindSpeed.None]: 0,
    [WindSpeed.Light]: 0,
    [WindSpeed.Moderate]: 0,      // IMPS had no spotting below Strong (calc-wind.js)
    [WindSpeed.Strong]: 0.7
};

export const windConstants = getWindConstantsWithOverrides({
    windInfluence: baseWindInfluence,
    emberThrowWeight: baseEmberThrowWeight
});

export const {windInfluence, emberThrowWeight} = windConstants;

export type GetWindInfluenceParams = {
    windSpeed: WindSpeed;
    windDirection: Vec2;
    cellDirection: Vec2;
};
export function getWindInfluence({windSpeed, windDirection, cellDirection}: GetWindInfluenceParams): number {
    const windData = windInfluence[windSpeed];
    const dotProduct = Math.round(getDotProduct(getUnitVector(windDirection), getUnitVector(cellDirection)) * 10);
    if(dotProduct >= 9) {
        // 10 is sirectly in front of the wind
        return windData[0];
    }
    if(dotProduct >= 5) {
        // 7 is 45 degrees
        return windData[1];
    }
    if(dotProduct >= -4) {
        // 0 is perpendicular to the wind
        return windData[2];
    }
    if(dotProduct >= -8) {
        // -7 is 45 degrees behind the wind
        return windData[3];
    }
    if(dotProduct >= -10) {
        // -10 is directly behind the wind
        return windData[4];
    }
    logger.log(`Invalid dot product: ${JSON.stringify({
        windSpeed,
        windDirection,
        cellDirection,
        windUnit: getUnitVector(windDirection),
        cellUnit: getUnitVector(cellDirection),
        dotProduct: getDotProduct(getUnitVector(windDirection), getUnitVector(cellDirection))
    })}`, LogLevel.Error);
    throw new Error(`Invalid dot product ${dotProduct}`);
}

export type GetThrowDestParams = {
    windSpeed: WindSpeed;
    windDirection: Vec2;
    originCoords: Coords;
    terrain?: Terrain<TerrainCodes>
};

export function getThrowDest({windSpeed, windDirection, originCoords, terrain}: GetThrowDestParams): Coords{

    if(!terrain?.spotDistanceDistribution || terrain.spotDistanceDistribution.length == 0) {
        return originCoords;
    }

    const spotChances = terrain.spotDistanceDistribution;
    const noSpotChance = spotChances.reduce((acc, segment) => {
        return acc - segment.weight;
    }, 1); // work out how many odds are left over from the segments
    let prob = Math.random();
    logger.log(`Prob: ${prob} for ${windSpeed}`, LogLevel.Debug);
    if(prob <= noSpotChance) {
        return originCoords;
    }
    if(noSpotChance < 0) {
        logger.log(`Spot segments for ${terrain.code} are greater than 1`, LogLevel.Warn);
    } else {
        prob -= noSpotChance;
    }
    let segment = spotChances.find((segment) => {
        if(prob < segment.weight) {
            return true;
        }
        prob -= segment.weight;
        return false;
    });
    if(!segment) {
        logger.log(`Reached the end of segments without maching on for terrain ${JSON.stringify(terrain)}`, LogLevel.Error);
        return originCoords;
    }
    logger.log(JSON.stringify(segment), LogLevel.Debug);
    const {maxDistance, minDistance=0} = segment;
    const throwDistance = ((Math.random() * (maxDistance - minDistance)) + minDistance) * emberThrowWeight[windSpeed];
    logger.log(`Throw distance: ${throwDistance}: Max: ${maxDistance}, Min: ${minDistance}, weight: ${emberThrowWeight[windSpeed]}`, LogLevel.Debug);
    const scaledWind = multiplyMagnitude(windDirection, throwDistance);
    return {
        x: Math.round(originCoords.x + scaledWind[0]),
        y: Math.round(originCoords.y + scaledWind[1])
    };
}


